import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useContent } from '../contexts/ContentContext';
import './ContactPage.css';

const ContactPage = () => {
  const { i18n } = useTranslation();
  const { content, submitMessage } = useContent();
  const isSk = i18n.language?.startsWith('sk');
  const c = content.contact;

  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const ok = await submitMessage(form);
    setSending(false);
    if (ok) {
      toast.success(isSk ? 'Správa bola úspešne odoslaná!' : 'Message successfully sent!');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } else {
      toast.error(isSk ? 'Pri odosielaní nastala chyba.' : 'An error occurred.');
    }
  };

  return (
    <>
      <Helmet>
        <title>{isSk ? 'Kontakt | ZEL s.r.o.' : 'Contact Us | ZEL s.r.o.'}</title>
        <meta name="description" content={isSk
          ? 'Kontaktujte ZEL s.r.o. — Pri smaltovni 3603/4, Bratislava-Petržalka.'
          : 'Contact ZEL s.r.o. — Pri smaltovni 3603/4, Bratislava-Petržalka.'} />
        <link rel="canonical" href="https://www.zel.sk/contact-us" />
      </Helmet>

      <Navbar />

      <main className="contact-page">
        {/* HERO — exact zel.sk: just "Contact Us" */}
        <section className="contact-hero">
          <div className="container">
            <h1>{isSk ? 'Kontakt' : 'Contact Us'}</h1>
          </div>
        </section>

        {/* CONTACT DETAILS — exact zel.sk content: hours + phone + email + address + Visit Us */}
        <section className="contact-details section">
          <div className="container contact-details__grid">
            <div className="contact-details__col">
              <div className="cd-block">
                <FaClock className="cd-block__icon" />
                <div>
                  <h3>{isSk ? 'Otváracie hodiny' : 'Business Hours'}</h3>
                  <p>Mon-Fri: 9:00AM - 5:00PM</p>
                  <p>Sat-Sun: 10:00AM - 6:00PM</p>
                </div>
              </div>

              <div className="cd-block">
                <FaPhoneAlt className="cd-block__icon" />
                <div>
                  <h3>{isSk ? 'Telefón' : 'Phone'}</h3>
                  <p>+90 542 647 34 87</p>
                  <p>{c.phone}</p>
                </div>
              </div>

              <div className="cd-block">
                <FaEnvelope className="cd-block__icon" />
                <div>
                  <h3>{isSk ? 'E-mail' : 'Email'}</h3>
                  <p>{c.email}</p>
                </div>
              </div>

              <div className="cd-block">
                <FaMapMarkerAlt className="cd-block__icon" />
                <div>
                  <h3>{isSk ? 'Navštívte nás' : 'Visit Us'}</h3>
                  <p>ZEL s. r. o.</p>
                  <p>Pri smaltovni 3603/4</p>
                  <p>851 01 Bratislava – Petržalka</p>
                  <p>{isSk ? 'Slovensko' : 'Slovakia'}</p>
                  <p className="cd-block__tagline">
                    {isSk ? 'Postavme niečo, čo funguje' : "Let's Build Something That Works"}
                  </p>
                </div>
              </div>
            </div>

            <form className="contact-form" onSubmit={onSubmit}>
              <h3>{isSk ? 'Napíšte nám' : 'Send us a message'}</h3>

              <div className="form-row">
                <div className="form-field">
                  <label>{isSk ? 'Meno' : 'Name'} *</label>
                  <input name="name" required value={form.name} onChange={onChange} placeholder={isSk ? 'Vaše meno' : 'Your name'} />
                </div>
                <div className="form-field">
                  <label>{isSk ? 'E-mail' : 'Email'} *</label>
                  <input name="email" type="email" required value={form.email} onChange={onChange} placeholder="email@example.com" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>{isSk ? 'Telefón' : 'Phone'}</label>
                  <input name="phone" value={form.phone} onChange={onChange} placeholder="+421 ..." />
                </div>
                <div className="form-field">
                  <label>{isSk ? 'Predmet' : 'Subject'}</label>
                  <input name="subject" value={form.subject} onChange={onChange} placeholder={isSk ? 'Predmet správy' : 'Subject'} />
                </div>
              </div>

              <div className="form-field">
                <label>{isSk ? 'Správa' : 'Message'} *</label>
                <textarea name="message" rows="6" required value={form.message} onChange={onChange} placeholder={isSk ? 'Napíšte nám detaily...' : 'Write us details...'} />
              </div>

              <button type="submit" className="btn btn-primary" disabled={sending}>
                {sending
                  ? (isSk ? 'Odosielam...' : 'Sending...')
                  : <>{isSk ? 'ODOSLAŤ SPRÁVU' : 'SEND MESSAGE'} <FaPaperPlane /></>}
              </button>
            </form>
          </div>
        </section>

        {/* VISIT US — map section */}
        <section className="contact-map-section">
          <div className="container contact-map-section__head">
            <h2>{isSk ? 'Navštívte nás' : 'Visit Us'}</h2>
            <p>
              ZEL s. r. o., Pri smaltovni 3603/4, 851 01 Bratislava – Petržalka, {isSk ? 'Slovensko' : 'Slovakia'}
            </p>
            <p className="contact-map-section__tagline">
              {isSk ? 'Postavme niečo, čo funguje' : "Let's Build Something That Works"}
            </p>
          </div>

          <div className="contact-map">
            <iframe
              title="ZEL s.r.o. location"
              src="https://www.google.com/maps?q=Pri+smaltovni+3603%2F4,+851+01+Bratislava&output=embed"
              width="100%"
              height="500"
              style={{ border: 0, display: 'block' }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default ContactPage;
